import { useState } from "react";
import { ReportAPI } from "../../../../axios/api";
import { useAbortedEffect } from "../../../../components/hooks";
import moment from "moment";

import {
  useSearchFilter,
  ModuleSearchFilterWrap,
} from "../../../../components/searchFilter";
import ModuleSectionBlock from "../../../../components/sectionBlock";
import ModuleBlockLoading from "../../../../components/loading";
import {
  ModuleChartPie,
  ModuleChartPieTool,
} from "../../../../components/chart";
import { option_colors } from "../../../../service/option";
import { decimalAddZero } from "../../../../service/common";

const renderEnergyRatePieToolRow = ({ item, index, isTenThousandUnit }) => (
  <tr key={`tool_${item.name ?? index}`} className="text-gray line-height-3">
    <td>
      <div className="flex align-items-center">
        <div
          className="inline-block w-1rem h-1rem"
          style={{ backgroundColor: option_colors[index] }}
        ></div>
        <span className="ml-2">{item.name}</span>
      </div>
    </td>
    <td className="text-right px-3">
      {`${decimalAddZero({
        rawVal: (item.value ?? 0) / (isTenThousandUnit ? 10000 : 1),
        decimalCount: 0,
      })} ${isTenThousandUnit ? "萬" : ""}${item.unit}`}
    </td>
    <td className="text-right">{item.percent}%</td>
  </tr>
);

export default function ModuleCustomChartEnergyRatePie() {
  const [reload, setReload] = useState(false);
  const { filterData, searchData, setSearchData, changeFilter } =
    useSearchFilter({
      defaultFilter: {
        period: 1,
        startDate: moment().startOf("month").format("YYYY-MM-DD"),
        endDate: moment().endOf("month").format("YYYY-MM-DD"),
      },
      searchKey: ["usageStatistic"],
      reloadAction: () => setReload(true),
    });
  const filterColumns = [
    {
      ui: "calendar",
      uiClassName: "w-9rem",
      label: "起始",
      params: {
        name: "startDate",
        // maxKey: "endDate",
        dateEffectKey: "endDate",
        dateDirection: "start",
        dateEffectNumber: null,
        dateEffectUnit: "months",
      },
    },
    {
      ui: "calendar",
      uiClassName: "w-9rem",
      label: "結束",
      params: {
        name: "endDate",
        // minKey: "startDate",
        dateEffectKey: "startDate",
        dateDirection: "end",
        dateEffectNumber: null,
        dateEffectUnit: "months",
      },
    },
  ];
  const [apiData, setApiData] = useState({ keys: [], data: [] });
  const renderToolRow = ({ data: item, index }) =>
    renderEnergyRatePieToolRow({
      item,
      index,
      isTenThousandUnit: apiData.isTenThousandUnit,
    });

  useAbortedEffect(
    (signal) => {
      if (!reload) return;

      callAPI({ signal });
    },
    [reload],
  );

  const callAPI = ({ signal }) => {
    const params = {
      startDate: searchData.startDate,
      endDate: searchData.endDate,
      groupBuildingFloor: [],
    };

    ReportAPI.getEnergyShare({
      options: { signal },
      data: params,
    }).then((result) => {
      let { Response, data } = result;

      if (Response === 1) {
        formatHandler({ data });
        setReload(false);
      }
    });
  };

  const formatHandler = ({ data }) => {
    const totalEnergy = Number(data.totalEnergy) || 0;
    const formatData = [...data.items]
      .sort((left, right) => {
        if (left.energyID == null && right.energyID == null) return 0;
        if (left.energyID == null) return 1;
        if (right.energyID == null) return -1;
        return Number(left.energyID) - Number(right.energyID);
      })
      .map((item) => ({
        name: item.energyName,
        value: Number(item.totalEnergy) || 0,
        percent: item.percentage ?? 0,
        unit: "kWh",
      }));

    setApiData({
      isTenThousandUnit: totalEnergy > 30000,
      total: totalEnergy,
      data: formatData,
    });
  };

  return (
    <ModuleSectionBlock
      targetId="energyRatePie"
      isExpand={false}
      className="h-full"
      title="用量能源佔比"
      isExport={true}
      exportOptions={[
        {
          isList: true,
          listOptions: [
            {
              listColumns: [
                ...apiData.data.map((item) => ({
                  field: item.name,
                  header: item.name,
                  exportOptions: {
                    width: 15,
                  },
                })),
              ],
              listData: [
                apiData.data.reduce(
                  (pre, cur) => ({ ...pre, [cur.name]: cur.value }),
                  {},
                ),
              ],
              listPos: { x: 0, y: 1 },
            },
          ],
          isImg: true,
          imgOptions: [
            {
              imgId: "energyRatePie",
              imgPos: { x: 1 + apiData.data.length, y: 1 },
            },
          ],
        },
      ]}
      overlaySetting={[
        {
          icon: "pi pi-filter",
          template: (
            <ModuleSearchFilterWrap
              className="w-auto text-white"
              columns={filterColumns}
              filterData={filterData}
              setSearchData={setSearchData}
              changeFilter={changeFilter}
            />
          ),
        },
      ]}
      bodyTemplate={
        <>
          {reload && <ModuleBlockLoading />}

          <div className="flex align-items-center w-full">
            <ModuleChartPie
              className="w-15rem md:w-10rem xl:w-15rem h-15rem"
              data={apiData.data}
              isTenThousandUnit={apiData.isTenThousandUnit}
            />
            <ModuleChartPieTool
              data={apiData.data}
              headTemplate={
                <div className="text-lg text-gray font-bold mb-2">
                  {`總用電量：${decimalAddZero({
                    rawVal:
                      (apiData.total ?? 0) /
                      (apiData.isTenThousandUnit ? 10000 : 1),
                    decimalCount: 0,
                  })} ${apiData.isTenThousandUnit ? "萬" : ""}kWh`}
                </div>
              }
              bodyTemplate={renderToolRow}
            />
          </div>
        </>
      }
    />
  );
}
